import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { Invoice } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { LoadFailed, NothingYet } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { ChartCard, FigureTable } from "@/components/charts/ChartCard";
import { CategoryBarChart, RankedBarChart } from "@/components/charts/lazy";
import { formatCompactMoney, formatRupees } from "@/lib/chart-format";
import { formatMoney } from "@/modules/gst/format";
import { aggregateHsnSummary } from "@/modules/gst/hsnSummary";
import { aggregateByTaxKind, taxKindLabel } from "@/modules/reports/gst";
import { fetchAllPages } from "@/modules/reports/lib/pagination";
import { ReportHeader } from "@/modules/reports/ReportHeader";

async function loadInvoices(
  data: ReturnType<typeof useDataSource>,
): Promise<Invoice[]> {
  return fetchAllPages((query) => data.invoicing.list(query));
}

/**
 * Taxable value and tax by HSN code and by tax kind (FR-GST-14, ticket #96).
 *
 * By-HSN reuses `aggregateHsnSummary` from the GST module rather than
 * grouping invoice lines again here — a second grouping function that
 * happened to filter `draft`/`cancelled` differently, or split on `gstRate`
 * differently, would let this report and GST's own HSN Summary screen show
 * two different totals for the same period with no way to tell which one is
 * wrong.
 */
export function Gst() {
  const data = useDataSource();

  const query = useQuery({
    queryKey: ["reports", "gst"],
    queryFn: () => loadInvoices(data),
  });

  return (
    <div className="flex flex-col gap-6">
      <ReportHeader readAt={query.data ? query.dataUpdatedAt : undefined} />
      <GstBody query={query} />
    </div>
  );
}

function GstBody({ query }: { query: UseQueryResult<Invoice[]> }) {
  if (query.isError) {
    return (
      <LoadFailed
        message={query.error.message}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  if (query.isPending) {
    return <TableSkeleton />;
  }

  const byHsn = aggregateHsnSummary(query.data);
  const byTaxKind = aggregateByTaxKind(query.data);

  if (byHsn.length === 0) {
    return (
      <NothingYet
        title="No issued invoices yet"
        description="Taxable value and tax appear here once invoices are issued."
      />
    );
  }

  const rupees = (value: number) => formatMoney(value.toFixed(2));

  return (
    <div className="flex flex-col gap-6">
      <ChartCard
        title="Tax collected by tax kind"
        description="CGST, SGST, IGST and cess on issued invoices, stacked into the tax each kind of sale carried. Sales inside the registered state split into CGST + SGST; sales to another state carry IGST. Drafts and cancelled invoices are left out."
        table={
          <FigureTable
            rows={byTaxKind}
            rowKey={(row) => row.taxKind}
            columns={[
              { header: "Tax kind", cell: (row) => taxKindLabel(row.taxKind) },
              {
                header: "Invoices",
                numeric: true,
                cell: (row) => row.invoiceCount,
              },
              {
                header: "Taxable value",
                numeric: true,
                cell: (row) => formatMoney(row.taxableValue),
              },
              {
                header: "CGST",
                numeric: true,
                cell: (row) => formatMoney(row.cgstAmount),
              },
              {
                header: "SGST",
                numeric: true,
                cell: (row) => formatMoney(row.sgstAmount),
              },
              {
                header: "IGST",
                numeric: true,
                cell: (row) => formatMoney(row.igstAmount),
              },
              {
                header: "Cess",
                numeric: true,
                cell: (row) => formatMoney(row.cessAmount),
              },
              {
                header: "Total",
                numeric: true,
                strong: true,
                cell: (row) => formatMoney(row.total),
              },
            ]}
          />
        }
      >
        <CategoryBarChart
          stacked
          categories={byTaxKind.map((row) => ({
            key: row.taxKind,
            label: taxKindLabel(row.taxKind),
            values: {
              cgst: Number(row.cgstAmount),
              sgst: Number(row.sgstAmount),
              igst: Number(row.igstAmount),
              cess: Number(row.cessAmount),
            },
          }))}
          series={[
            { key: "cgst", label: "CGST", color: "var(--series-1)" },
            { key: "sgst", label: "SGST", color: "var(--series-3)" },
            { key: "igst", label: "IGST", color: "var(--primary)" },
            { key: "cess", label: "Cess", color: "var(--series-5)" },
          ]}
          formatValue={rupees}
          formatTick={formatCompactMoney}
          ariaLabel={`Tax collected by tax kind: ${byTaxKind
            .map(
              (row) =>
                `${taxKindLabel(row.taxKind)} — CGST ${formatMoney(row.cgstAmount)}, SGST ${formatMoney(row.sgstAmount)}, IGST ${formatMoney(row.igstAmount)}, cess ${formatMoney(row.cessAmount)}`,
            )
            .join("; ")}.`}
        />
      </ChartCard>

      <ChartCard
        title="Taxable value by HSN code"
        description="The value tax was charged on, for each HSN code and rate on issued invoices, largest first. The same grouping as GST → HSN Summary, so the two always agree."
        table={
          <FigureTable
            rows={byHsn}
            rowKey={(row) => `${row.hsnCode}-${row.gstRate}`}
            columns={[
              { header: "HSN code", cell: (row) => row.hsnCode },
              {
                header: "Rate",
                numeric: true,
                cell: (row) => `${row.gstRate}%`,
              },
              { header: "Qty", numeric: true, cell: (row) => row.quantity },
              {
                header: "Taxable value",
                numeric: true,
                cell: (row) => formatMoney(row.taxableValue),
              },
              {
                header: "Total",
                numeric: true,
                strong: true,
                cell: (row) => formatMoney(row.total),
              },
            ]}
          />
        }
      >
        <RankedBarChart
          rows={[...byHsn]
            .sort((a, b) => Number(b.taxableValue) - Number(a.taxableValue))
            .map((row) => ({
              key: `${row.hsnCode}-${row.gstRate}`,
              label: `${row.hsnCode} · ${row.gstRate}%`,
              value: Number(row.taxableValue),
              note: `${String(row.quantity)} units · total ${formatMoney(row.total)}`,
            }))}
          valueLabel="Taxable value"
          formatValue={formatCompactMoney}
          formatExact={formatRupees}
          ariaLabel={`Taxable value by HSN code: ${byHsn
            .map(
              (row) =>
                `${row.hsnCode} at ${row.gstRate}% ${formatMoney(row.taxableValue)}`,
            )
            .join(", ")}.`}
        />
      </ChartCard>
    </div>
  );
}
